import styled, { css } from 'styled-components';

/* Yleiset rakennuspalikat. Värit ja säteet tulevat myyntihaku.fi-teemasta. */

export const Panel = styled.div<{ $max?: string }>`
  background: ${p => p.theme.color.card};
  border: 1px solid ${p => p.theme.color.line};
  border-radius: ${p => p.theme.radius.xl};
  padding: 16px;
  box-shadow: ${p => p.theme.shadow};
  ${p => p.$max && css`max-width: ${p.$max};`}
`;

export const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(150px, 100%), 1fr));
  gap: 12px;
  margin-bottom: 8px;
`;

export const Card = styled.div<{ $korostus?: boolean }>`
  background: ${p => p.theme.color.cardSage};
  border: ${p => (p.$korostus ? '2px' : '1px')} solid
          ${p => (p.$korostus ? p.theme.color.accent : '#C5D3BB')};
  border-radius: ${p => p.theme.radius.md};
  padding: 14px 16px;
  .num { font-size: 26px; font-weight: 650; ${p => p.$korostus && css`color: ${p.theme.color.accentInk};`} }
  .lbl { color: ${p => p.theme.color.muted}; font-size: 13px; }
`;

const painike = css`
  font: inherit;
  font-weight: 600;
  border: 1px solid ${p => p.theme.color.line};
  background: transparent;
  color: ${p => p.theme.color.ink};
  border-radius: ${p => p.theme.radius.md};
  padding: 8px 14px;
  min-height: 36px;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${p => p.theme.color.hover}; }
  &:disabled { opacity: .55; cursor: not-allowed; }
`;

type NappiProps = { $variant?: 'primary' | 'danger'; $small?: boolean };
const variantti = css<NappiProps>`
  ${p => p.$variant === 'primary' && css`
    background: ${p.theme.color.ink};
    border-color: ${p.theme.color.ink};
    color: ${p.theme.color.card};
    &:hover { background: #2A2A2A; }
  `}
  ${p => p.$variant === 'danger' && css`color: ${p.theme.color.red};`}
  ${p => p.$small && css`padding: 4px 10px; font-size: 12px; min-height: 0;`}
`;

export const Button = styled.button<NappiProps>`${painike}${variantti}`;
/** Aito linkki napin näköisenä — ponnahdusikkunoiden esto ei koske sitä. */
export const LinkButton = styled.a<NappiProps>`${painike}${variantti}`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 10px 0;
  align-items: center;
`;

export const Toolbar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
  align-items: center;
  @media (max-width: 640px) {
    > input, > select { flex: 1 1 100%; width: 100%; min-width: 0; max-width: none; }
  }
`;

export const Muted = styled.span`color: ${p => p.theme.color.muted};`;

export const Hint = styled.p`
  font-size: 12.5px;
  color: ${p => p.theme.color.muted};
  margin: 6px 0;
`;

export const Warn = styled.div`
  font-size: 12.5px;
  color: ${p => p.theme.color.accentInk};
  background: #F8EFE8;
  border: 1px solid #E8D3C4;
  border-radius: ${p => p.theme.radius.md};
  padding: 8px 12px;
  margin: 6px 0;
`;

export const Empty = styled.div`
  color: ${p => p.theme.color.muted};
  padding: 14px 4px;
`;

export const Field = styled.div`
  margin-bottom: 9px;
  label {
    display: block;
    font-size: 13px;
    font-weight: 550;
    color: ${p => p.theme.color.inkSoft};
    margin-bottom: 4px;
  }
  > input, > select { width: 100%; }
`;

export const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 12px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

export const TableWrap = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;

export const Table = styled.table<{ $eiKlikattava?: boolean }>`
  width: 100%;
  border-collapse: collapse;
  background: ${p => p.theme.color.card};
  border: 1px solid ${p => p.theme.color.line};
  border-radius: ${p => p.theme.radius.xl};
  overflow: hidden;
  th, td { text-align: left; padding: 9px 12px; border-bottom: 1px solid ${p => p.theme.color.line}; }
  th {
    background: ${p => p.theme.color.hover};
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: ${p => p.theme.color.muted};
  }
  tbody tr { cursor: ${p => (p.$eiKlikattava ? 'default' : 'pointer')}; }
  ${p => !p.$eiKlikattava && css`tbody tr:hover { background: ${p.theme.color.rowHover}; }`}
`;

export const Chip = styled.span<{ $bg?: string }>`
  display: inline-block;
  border-radius: ${p => p.theme.radius.pill};
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: #fff;
  background: ${p => p.$bg || p.theme.color.muted};
`;

export const NeutralChip = styled(Chip)`
  background: ${p => p.theme.color.chipBg};
  color: ${p => p.theme.color.ink};
  font-weight: 500;
`;

export const CopyChips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin: 8px 0;
  button {
    font: inherit;
    font-size: 12px;
    border: 1px dashed ${p => p.theme.color.sage};
    background: ${p => p.theme.color.card};
    border-radius: ${p => p.theme.radius.pill};
    padding: 3px 10px;
    cursor: pointer;
    &:hover { border-color: ${p => p.theme.color.accent}; background: ${p => p.theme.color.rowHover}; }
  }
`;

export const Due = styled.span`
  color: ${p => p.theme.color.red};
  font-weight: 600;
`;
